import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  X,
  Home,
  Calculator,
  Palette,
  Shield,
  FileText,
  Globe,
  Image,
  Type,
  Code,
  Settings,
  HelpCircle,
  ExternalLink,
  BookOpen
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { tabs as contentTabs } from '../pages/Content';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/',
      icon: Home,
      description: 'Visão geral das ferramentas'
    },
    {
      name: 'Utilidades',
      href: '/utils',
      icon: Calculator,
      description: 'Calculadora, gerador de senhas, etc.',
      children: [
        { name: 'Calculadora', href: '/utils/calculator' },
        { name: 'Gerador de Senhas', href: '/utils/password-generator' },
        { name: 'Gerador de UUID', href: '/utils/uuid' },
        { name: 'Hash de Texto', href: '/utils/hash' },
        { name: 'Base64', href: '/utils/base64' },
        { name: 'URL Encoder', href: '/utils/url-encoder' },
        { name: 'Lorem Ipsum', href: '/utils/lorem-ipsum' },
        { name: 'Consulta de MAC Address', href: '/utils/mac-lookup' },
        { name: 'Gerador de MAC Address', href: '/utils/mac-generator' },
        { name: 'Gerador de IPv6 ULA', href: '/utils/ipv6-ula-generator' },
        { name: 'Calculadora Avançada', href: '/utils/math-evaluator' },
        { name: 'Calculadora de ETA', href: '/utils/eta-calculator' },
        { name: 'Calculadora de Porcentagem', href: '/utils/percentage-calculator' },
        { name: 'Cronômetro', href: '/utils/chronometer' },
        { name: 'Validador de Telefone', href: '/utils/phone-validator' },
        { name: 'Validador de IBAN', href: '/utils/iban-validator' },
        { name: 'User Agent Finder', href: '/utils/user-agent-finder' },
        { name: 'Qual é Meu IP?', href: '/utils/my-ip' },
        { name: 'Ping', href: '/utils/ping' },
        { name: 'Achar URL Encurtada', href: '/utils/unshorten-url' },
      ]
    },
    {
      name: 'Conversores',
      href: '/converters',
      icon: Palette,
      description: 'Conversores de cores, unidades, etc.',
      children: [
        { name: 'Conversor de Cores', href: '/converters/colors' },
        { name: 'Conversor de Unidades', href: '/converters/units' },
        { name: 'Conversor de Moedas', href: '/converters/currency' },
        { name: 'Conversor de Tempo', href: '/converters/time' },
        { name: 'Conversor de Temperatura', href: '/converters/temperature' }
      ]
    },
    {
      name: 'Segurança',
      href: '/security',
      icon: Shield,
      description: 'Validador de senhas, CPF, etc.',
      children: [
        { name: 'Validador de Senha', href: '/security/password' },
        { name: 'Validador de Email', href: '/security/email' },
        { name: 'Validador de CPF', href: '/security/cpf' },
        { name: 'Validador de CNPJ', href: '/security/cnpj' },
        { name: 'Hash Seguro', href: '/security/hash' },
        { name: 'Verificar Hash', href: '/security/verify' }
      ]
    },
    {
      name: 'Conteúdo',
      href: '/content',
      icon: FileText,
      description: 'Formatadores e analisadores de texto',
      children: contentTabs.map(tab => ({ name: tab.name, href: tab.path }))
    },
    {
      name: 'Domínios',
      href: '/domains',
      icon: Globe,
      description: 'Verificadores de DNS, SSL, etc.',
      children: [
        { name: 'Verificador de DNS', href: '/domains/dns' },
        { name: 'Disponibilidade de Domínio', href: '/domains/availability' },
        { name: 'Verificador de Redirecionamento', href: '/domains/redirect' },
        { name: 'Gerador de Subdomínios', href: '/domains/subdomains' },
        { name: 'Tempo de Resposta', href: '/domains/response' }
      ]
    },
    {
      name: 'Imagens',
      href: '/images',
      icon: Image,
      description: 'Conversores e processadores de imagem',
      children: [
        { name: 'Conversor de Formato', href: '/images/convert' },
        { name: 'Redimensionador', href: '/images/resize' },
        { name: 'Compressor', href: '/images/compress' },
        { name: 'Gerador de Thumbnail', href: '/images/thumbnail' },
        { name: 'Analisador de Imagem', href: '/images/analyze' },
        { name: 'Gerador de QR Code', href: '/images/qr' }
      ]
    },
    {
      name: 'Texto',
      href: '/text',
      icon: Type,
      description: 'Formatadores de JSON, XML, SQL, etc.',
      children: [
        { name: 'Formatador de JSON', href: '/text/json-formatter' },
        { name: 'Validador de JSON', href: '/text/json-validator' },
        { name: 'Formatador de XML', href: '/text/xml-formatter' },
        { name: 'Validador de XML', href: '/text/xml-validator' },
        { name: 'Formatador de SQL', href: '/text/sql-formatter' },
        { name: 'Formatador de CSV', href: '/text/csv-formatter' },
        { name: 'Conversor de Texto', href: '/text/text-converter' },
        { name: 'Seletor de Emojis', href: '/text/emoji-picker' },
        { name: 'Ofuscador de String', href: '/text/string-obfuscator' },
        { name: 'Gerador de Numerônimos', href: '/text/numeronym-generator' },
        { name: 'Gerador de ASCII Art', href: '/text/ascii-art-generator' },
        { name: 'Gerador de Textos Estilizados', href: '/text/font-style-generator' }
      ]
    },
    {
      name: 'Desenvolvimento',
      href: '/development',
      icon: Code,
      description: 'Geradores de código e validadores',
      children: [
        { name: 'Gerador de CSS', href: '/development/css' },
        { name: 'Validador de Regex', href: '/development/regex' },
        { name: 'Gerador de API Mock', href: '/development/api-mock' },
        { name: 'Gerador de SQL', href: '/development/sql' },
        { name: 'Gerador de UUID', href: '/development/uuid' },
        { name: 'Senha Forte', href: '/development/password' },
        { name: 'Git Cheatsheet', href: '/development/git-cheatsheet' },
        { name: 'Construtor de Benchmark', href: '/development/benchmark-builder' }
      ]
    }
  ];

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isChildActive = (childHref) => {
    if (childHref.includes('?tab=')) {
      const [path, query] = childHref.split('?');
      const urlParams = new URLSearchParams(location.search);
      const tabParam = urlParams.get('tab');
      return location.pathname === path && tabParam === query.split('=')[1];
    }
    return location.pathname === childHref;
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        <motion.aside
          initial={{ x: -300 }}
          animate={{ x: 0 }}
          exit={{ x: -300 }}
          transition={{ type: 'spring', damping: 20, stiffness: 100 }}
          className={`fixed left-0 top-0 z-50 h-full w-80 bg-white border-r border-gray-200 shadow-lg lg:relative lg:translate-x-0 ${
            isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
        >

          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-4 space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive(item.href)
                        ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-500'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon size={18} />
                    <span>{item.name}</span>
                  </Link>

                  {item.children && isActive(item.href) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-6 mt-2 space-y-1"
                    >
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className={`block px-3 py-2 text-sm rounded-lg transition-colors ${
                            isChildActive(child.href)
                              ? 'bg-primary-100 text-primary-700'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </nav>

          <div className="border-t border-gray-200 p-4">
            <div className="space-y-2">

            <a
                href="https://nservers.io"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
              >
                <ExternalLink size={18} />
                <span>nServers - Site Oficial</span>
              </a>

              <a
                href="https://tools.nservers.io"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
              >
                <ExternalLink size={18} />
                <span>nServers Tools</span>
              </a>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                nServers Tools v1.0.0
              </p>
            </div>
          </div>
        </motion.aside>
      </AnimatePresence>
    </>
  );
};

export default Sidebar; 